import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { getEnduserAllSessions } from "../state/enduserStatsSlice";
import type { DateRange } from "../../statsNew/components/DateRangePicker";
import { isSessionInDateRange } from "../../statsNew/utils/dateUtils";

interface EnduserSessionsPerMonthChartProps {
  dateRange: DateRange;
}

interface MonthData {
  month: number;
  year: number;
  label: string;
  fullLabel: string;
  sessions: number;
}

const EnduserSessionsPerMonthChart = ({ dateRange }: EnduserSessionsPerMonthChartProps) => {
  const { t } = useTranslation();
  const allSessions = useSelector(getEnduserAllSessions);

  // Find the most recent session date to initialize the view
  const mostRecentSessionDate = useMemo(() => {
    if (allSessions.length === 0) return new Date();

    const sortedSessions = [...allSessions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return new Date(sortedSessions[0].createdAt);
  }, [allSessions]);

  // State for the end date of the 12-month view window
  const [viewEndDate, setViewEndDate] = useState(new Date());
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize view to show the period with the most recent session
  useEffect(() => {
    if (allSessions.length > 0 && !hasInitialized) {
      setViewEndDate(mostRecentSessionDate);
      setHasInitialized(true);
    }
  }, [allSessions, mostRecentSessionDate, hasInitialized]);

  // Reset view when dateRange changes
  useEffect(() => {
    if (hasInitialized) {
      setViewEndDate(mostRecentSessionDate);
    }
  }, [dateRange, mostRecentSessionDate, hasInitialized]);

  const monthLabels = useMemo(
    () => [
      t("pages.statistics.months.january"),
      t("pages.statistics.months.february"),
      t("pages.statistics.months.march"),
      t("pages.statistics.months.april"),
      t("pages.statistics.months.may"),
      t("pages.statistics.months.june"),
      t("pages.statistics.months.july"),
      t("pages.statistics.months.august"),
      t("pages.statistics.months.september"),
      t("pages.statistics.months.october"),
      t("pages.statistics.months.november"),
      t("pages.statistics.months.december"),
    ],
    [t],
  );

  const chartData = useMemo(() => {
    // Generate 12 months ending at viewEndDate
    const months: MonthData[] = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - i, 1);
      const monthData: MonthData = {
        month: date.getMonth(),
        year: date.getFullYear(),
        label: `${monthLabels[date.getMonth()].slice(0, 3)} ${date
          .getFullYear()
          .toString()
          .slice(-2)}`,
        fullLabel: `${monthLabels[date.getMonth()]} ${date.getFullYear()}`,
        sessions: 0,
      };
      months.push(monthData);
    }

    // Count sessions per month
    allSessions.forEach((session) => {
      const sessionDate = new Date(session.createdAt);

      // Apply date range filter
      if (!isSessionInDateRange(sessionDate, dateRange)) return;

      const sessionMonth = sessionDate.getMonth();
      const sessionYear = sessionDate.getFullYear();

      const monthData = months.find((m) => m.month === sessionMonth && m.year === sessionYear);
      if (monthData) {
        monthData.sessions++;
      }
    });

    return months;
  }, [allSessions, dateRange, monthLabels, viewEndDate]);

  const hasData = chartData.some((d) => d.sessions > 0);

  // Navigation handlers
  const goToPrevMonth = () => {
    setViewEndDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    const next = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() + 1, 1);
    if (next <= new Date()) {
      setViewEndDate(next);
    }
  };

  const goToPrevYear = () => {
    setViewEndDate((prev) => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
  };

  const goToNextYear = () => {
    const next = new Date(viewEndDate.getFullYear() + 1, viewEndDate.getMonth(), 1);
    if (next <= new Date()) {
      setViewEndDate(next);
    }
  };

  const canGoNext =
    new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() + 1, 1) <= new Date();

  // Get display period text
  const periodText = useMemo(() => {
    const startDate = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - 11, 1);
    const startStr = `${monthLabels[startDate.getMonth()].slice(0, 3)} ${startDate.getFullYear()}`;
    const endStr = `${monthLabels[viewEndDate.getMonth()].slice(
      0,
      3,
    )} ${viewEndDate.getFullYear()}`;
    return `${startStr} - ${endStr}`;
  }, [viewEndDate, monthLabels]);

  return (
    <div className="h-full flex flex-col">
      {/* Navigation controls */}
      <div className="flex-shrink-0 flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          {/* Previous year */}
          <button
            onClick={goToPrevYear}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={t("statsNew.charts.prevYear")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
              />
            </svg>
          </button>
          {/* Previous month */}
          <button
            onClick={goToPrevMonth}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title={t("statsNew.charts.prevMonth")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </div>

        <span className="text-sm text-gray-500 font-medium">{periodText}</span>

        <div className="flex items-center gap-1">
          {/* Next month */}
          <button
            onClick={goToNextMonth}
            disabled={!canGoNext}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={t("statsNew.charts.nextMonth")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {/* Next year */}
          <button
            onClick={goToNextYear}
            disabled={!canGoNext}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={t("statsNew.charts.nextYear")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Chart */}
      {!hasData ? (
        <div className="flex-1 flex items-center justify-center text-gray-500">
          {t("statsNew.charts.noData")}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%" className="flex-1">
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              allowDecimals={false}
              width={30}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              }}
              cursor={{ fill: "rgba(0, 200, 137, 0.1)" }}
              formatter={(value: number) => [value, t("statsNew.charts.sessions")]}
            />
            <Bar dataKey="sessions" fill="#00c889" radius={[4, 4, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default EnduserSessionsPerMonthChart;
